/* ══════════════════════════════════════════════════════════════
   CELEBRATE BUS

   The mark that celebrates lives in the sidebar; the things worth
   celebrating happen all over the app — a finished lesson deep inside
   a fullscreen overlay, a login inside AuthContext. Threading a prop
   from each of those down to the sidebar would mean touching every
   layer in between, so they publish here instead and the sidebar
   subscribes.

   A plain module rather than a context, for two reasons: AuthContext
   fires this and would otherwise have to sit *inside* a provider it
   currently wraps, and publishing causes no re-render of anything that
   is not actually listening.

   The pending slot is the part that matters. Logging in fires this
   while the sidebar does not exist yet — the user is still on /login,
   and the app only mounts the shell after the redirect. Without
   somewhere to park that event it would simply be lost, so an unheard
   fire waits briefly for the first listener to claim it.
══════════════════════════════════════════════════════════════ */

const listeners = new Set();

let pendingAt = 0;
/* Long enough to cover a login redirect and the shell's first paint,
   short enough that a fire from a previous session cannot leak into a
   later one. */
const CLAIM_WINDOW_MS = 6000;

let pendingName = 'celebrate';

/* `name` is any entry in the mark's ANIMATION_NAMES — 'bounce' for a
   correct answer, 'shake' for a wrong one, 'nod' for a save. Defaults to
   the full celebration so the original call sites keep working. */
export function playMark(name = 'celebrate') {
  if (listeners.size > 0) {
    listeners.forEach(fn => fn(name));
    return;
  }
  pendingAt = Date.now();
  pendingName = name;
}

/* Kept as the name most call sites already use. */
export function celebrate() {
  playMark('celebrate');
}

export function onCelebrate(fn) {
  listeners.add(fn);

  /* Claim before calling, so React's StrictMode double-subscribe cannot
     fire the same event twice. */
  if (pendingAt && Date.now() - pendingAt < CLAIM_WINDOW_MS) {
    pendingAt = 0;
    fn(pendingName);
  }

  return () => listeners.delete(fn);
}
